import {
  createListRegistrants,
  PaginatedListRegistrantResponseList,
  RegistrantFilterRequest,
  createRegistrantBulkBlock,
  createRegistrantBulkUnblock,
  createRegistrantBulkDelete,
  createChangeRegistrantsStatus,
  createRegistrantsSendEmails,
  UpdatedStatusEnum,
  RegistrantsColumn
} from '@goldcast/api/backend';

import { EmailPayload } from '@/studio/eventRegistrants/EmailEventRegistrants.vue';
import { getEnv } from '@/utils/env';

export const REGISTRANTS_PER_PAGE = 15;
export const COLUMNS_TO_REMOVE: string[] = ['entered', 'profile_picture_url'];
export const ALLOW_SEARCH_COLUMNS: string[] = ['fullname', 'email'];
export const ALLOW_SORT_COLUMNS: string[] = ['fullname', 'email', 'created_at'];
export const DONT_ALLOW_FILTER_COLUMNS: string[] = ['fullname', 'email', 'created_at', 'magic_link'];
export const STANDARD_BOOLEAN_COLUMNS: string[] = ['blocked', 'attended', 'in_person_attended_time'];
export const TICKET_COLUMNS = ['ticket_labels', 'ticket_categories'];
export const MANDATORY_COLUMNS: string[] = ['fullname', 'email', 'event_role', 'title', 'company', 'created_at'];

export const ROLE = {
  ATTENDEE: 0,
  ORGANIZER: 2,
  SPEAKER: 4,
  SPONSOR: 6,
  CHECK_IN_STAFF: 8,
  STAFF: 9
};
export type SortBy = 'fullname' | '-fullname' | 'email' | '-email' | 'created_at' | '-created_at';
export type BulkOperation = 'BAN' | 'UNBAN' | 'DELETE' | 'STATUS_UPDATE' | 'SEND_EMAIL';

export const ROLE_ICONS = {
  [ROLE.ORGANIZER]: `${getEnv('VUE_APP_ASSETS_CDN')}/assets/icons/organizer_icon.svg`,
  [ROLE.SPEAKER]: `${getEnv('VUE_APP_ASSETS_CDN')}/assets/icons/speaker_icon.svg`,
  [ROLE.SPONSOR]: `${getEnv('VUE_APP_ASSETS_CDN')}/assets/icons/sponsor_icon.svg`,
  [ROLE.CHECK_IN_STAFF]: `${getEnv('VUE_APP_ASSETS_CDN')}/assets/icons/checkin_staff_icon.svg`,
  [ROLE.STAFF]: `${getEnv('VUE_APP_ASSETS_CDN')}/assets/icons/organizer_icon.svg`
};

export function fetchEventRegistrants({
  eventId,
  filters = {},
  sortBy = 'fullname',
  searchTerm = {},
  currentPage = 0,
  columnsApiResponse
}: {
  eventId: string;
  filters?: { [key: string]: string[] };
  sortBy?: SortBy;
  searchTerm?: { [key: string]: string };
  currentPage?: number;
  columnsApiResponse: RegistrantsColumn;
}): Promise<PaginatedListRegistrantResponseList> {
  const filtersArray: RegistrantFilterRequest[] = [];
  Object.entries(filters).forEach(([key, values]) => {
    if (values.length === 0) {
      return;
    }
    filtersArray.push({
      name: key,
      op: getFilterOperator(key, columnsApiResponse),
      values: getFilterValues(key, values, columnsApiResponse)
    });
  });
  Object.entries(searchTerm).forEach(([key, value]) => {
    if (!value) {
      return;
    }
    filtersArray.push({
      name: key,
      op: 'ICONTAINS',
      values: [value]
    });
  });

  return createListRegistrants({
    id: eventId,
    body: { filters: filtersArray, order_by: sortBy },
    queryParams: { offset: currentPage * REGISTRANTS_PER_PAGE, limit: REGISTRANTS_PER_PAGE }
  });
}

export function performBulkRegistrantsOperation({
  operation,
  eventId,
  searchTerm = {},
  filters = {},
  excludedRegistrantIds = [],
  selectedStatus = '',
  emailPayload,
  columnsApiResponse
}: {
  operation: BulkOperation;
  eventId: string;
  searchTerm: { [key: string]: string };
  filters: { [key: string]: string[] };
  excludedRegistrantIds: number[];
  selectedStatus?: UpdatedStatusEnum | '';
  emailPayload?: EmailPayload;
  columnsApiResponse: RegistrantsColumn;
}) {
  const filtersArray: RegistrantFilterRequest[] = [];
  Object.entries(filters).forEach(([key, values]) => {
    if (values.length === 0) {
      return;
    }
    filtersArray.push({
      name: key,
      op: getFilterOperator(key, columnsApiResponse),
      values: getFilterValues(key, values, columnsApiResponse)
    });
  });
  Object.entries(searchTerm).forEach(([key, value]) => {
    if (!value) {
      return;
    }
    filtersArray.push({
      name: key,
      op: 'ICONTAINS',
      values: [value]
    });
  });

  const operationActionMap: Record<BulkOperation, Function> = {
    BAN: createRegistrantBulkBlock,
    UNBAN: createRegistrantBulkUnblock,
    DELETE: createRegistrantBulkDelete,
    STATUS_UPDATE: createChangeRegistrantsStatus,
    SEND_EMAIL: createRegistrantsSendEmails
  };

  if (operation === 'STATUS_UPDATE') {
    return operationActionMap[operation]({
      id: eventId,
      body: {
        registrant_query: {
          filters: filtersArray,
          exclude_userids: excludedRegistrantIds
        },
        updated_status: selectedStatus
      }
    });
  }
  if (operation === 'SEND_EMAIL') {
    return operationActionMap[operation]({
      id: eventId,
      body: {
        query: {
          filters: filtersArray,
          exclude_userids: excludedRegistrantIds,
          include_userids: []
        },
        subject: emailPayload?.emailSubject,
        event_email_template: emailPayload?.emailTemplateId,
        users: []
      }
    });
  }

  return operationActionMap[operation]({
    id: eventId,
    body: {
      query: {
        filters: filtersArray,
        exclude_userids: excludedRegistrantIds
      }
    }
  });
}

const getFilterOperator = (key: string, columnsApiResponse: RegistrantsColumn): 'ICONTAINS' | 'EQUALS' =>
  TICKET_COLUMNS.includes(key) || columnsApiResponse.dict_prop_columns.includes(key) || ['event_role'].includes(key)
    ? 'ICONTAINS'
    : 'EQUALS';

const getFilterValues = (key: string, values: string[], columnsApiResponse: RegistrantsColumn): string[] => {
  if (columnsApiResponse.dict_prop_columns.includes(key)) {
    return values.map(value => `"${value}": true`);
  }
  return values;
};
